import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API = (process.env.REACT_APP_API_BASE || '').replace(/\/api$/, '') || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

export default function WhatsAppInboxPage() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open');
  const [showTest, setShowTest] = useState(false);
  const [testForm, setTestForm] = useState({ phone: '', name: '', text: '' });
  const threadRef = useRef(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` };

  const loadConversations = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const { data } = await axios.get(`${API}/api/saas/wa-inbox/conversations`, { headers, params });
      setConversations(data || []);
    } catch (err) { console.error(err); }
  };

  const loadMessages = async (convoId) => {
    try {
      const { data } = await axios.get(`${API}/api/saas/wa-inbox/conversations/${convoId}/messages`, { headers });
      setMessages(data.messages || []);
      setSelected(data.conversation);
      // Mark read
      await axios.post(`${API}/api/saas/wa-inbox/conversations/${convoId}/mark-read`, {}, { headers });
    } catch (err) { console.error(err); }
  };

  useEffect(() => {
    loadConversations();
    const timer = setInterval(loadConversations, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [statusFilter, search]);

  useEffect(() => {
    if (!selected) return;
    const timer = setInterval(() => loadMessages(selected._id), 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line
  }, [selected?._id]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!reply.trim() || !selected) return;
    try {
      await axios.post(`${API}/api/saas/wa-inbox/conversations/${selected._id}/send`, { text: reply }, { headers });
      setReply('');
      loadMessages(selected._id);
    } catch { alert('Send failed'); }
  };

  const setStatus = async (status) => {
    if (!selected) return;
    try {
      await axios.patch(`${API}/api/saas/wa-inbox/conversations/${selected._id}`, { status }, { headers });
      loadMessages(selected._id);
      loadConversations();
    } catch { alert('Update failed'); }
  };

  const simulateInbound = async () => {
    if (!testForm.phone || !testForm.text) return alert('Phone + text required');
    try {
      await axios.post(`${API}/api/saas/wa-inbox/simulate-inbound`, testForm, { headers });
      setTestForm({ phone: '', name: '', text: '' });
      setShowTest(false);
      loadConversations();
    } catch { alert('Failed'); }
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const s = (Date.now() - new Date(d).getTime()) / 1000;
    if (s < 60) return 'now';
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 80px)', background: '#f3f4f6' }}>
      {/* Column 1: Conversation list */}
      <div style={{ width: 320, borderRight: '1px solid #e5e7eb', background: 'white', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong>💬 Inbox</strong>
            <button onClick={() => setShowTest(true)} style={{ ...miniBtn, fontSize: 11 }}>🧪 Simulate</button>
          </div>
          <input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} style={{ ...input, marginBottom: 6 }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={input}>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="snoozed">Snoozed</option>
            <option value="">All</option>
          </select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {conversations.map(c => (
            <div key={c._id} onClick={() => loadMessages(c._id)}
              style={{ padding: 12, borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: selected?._id === c._id ? '#f0fdf4' : 'white' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <strong style={{ fontSize: 14 }}>{c.contactName || c.phone}</strong>
                <span style={{ fontSize: 11, color: '#666' }}>{timeAgo(c.lastMessageAt)}</span>
              </div>
              <div style={{ fontSize: 12, color: '#666', marginTop: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {c.lastMessage || '(no messages)'}
              </div>
              {c.unreadCount > 0 && (
                <span style={{ display: 'inline-block', background: '#25D366', color: 'white', padding: '2px 8px', borderRadius: 10, fontSize: 11, marginTop: 4 }}>
                  {c.unreadCount} new
                </span>
              )}
            </div>
          ))}
          {conversations.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#888', fontSize: 13 }}>
              No conversations.<br/>Use 🧪 Simulate to test.
            </div>
          )}
        </div>
      </div>

      {/* Column 2: Message thread */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {selected ? (
          <>
            <div style={{ padding: 12, borderBottom: '1px solid #e5e7eb', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{selected.contactName || selected.phone}</strong>
                <div style={{ fontSize: 12, color: '#666' }}>{selected.phone}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setStatus('open')} style={{ ...miniBtn, background: selected.status === 'open' ? '#dcfce7' : '#f3f4f6' }}>Open</button>
                <button onClick={() => setStatus('snoozed')} style={{ ...miniBtn, background: selected.status === 'snoozed' ? '#fef3c7' : '#f3f4f6' }}>Snooze</button>
                <button onClick={() => setStatus('closed')} style={{ ...miniBtn, background: selected.status === 'closed' ? '#fee2e2' : '#f3f4f6' }}>Close</button>
              </div>
            </div>
            <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#e7f3f0' }}>
              {messages.map(m => (
                <div key={m._id} style={{ display: 'flex', justifyContent: m.direction === 'outbound' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                  <div style={{
                    maxWidth: '70%', padding: '8px 12px', borderRadius: 8,
                    background: m.direction === 'outbound' ? '#dcf8c6' : 'white',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}>
                    {m.autoReplyRuleId && (
                      <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>🤖 Auto-reply</div>
                    )}
                    <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{m.text}</div>
                    <div style={{ fontSize: 10, color: '#888', textAlign: 'right', marginTop: 4 }}>
                      {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      {m.direction === 'outbound' && ` · ${m.status}`}
                    </div>
                  </div>
                </div>
              ))}
              {messages.length === 0 && <div style={{ textAlign: 'center', color: '#888', padding: 40 }}>No messages</div>}
            </div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', background: 'white', display: 'flex', gap: 8 }}>
              <input
                placeholder="Type reply..."
                value={reply}
                onChange={e => setReply(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                style={{ ...input, flex: 1, marginBottom: 0 }}
              />
              <button onClick={send} style={btnPrimary}>Send</button>
            </div>
          </>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888' }}>
            Select a conversation
          </div>
        )}
      </div>

      {/* Column 3: Contact details */}
      {selected && (
        <div style={{ width: 280, borderLeft: '1px solid #e5e7eb', background: 'white', padding: 16, overflowY: 'auto' }}>
          <h4 style={{ margin: '0 0 12px 0' }}>Contact</h4>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Name:</strong> {selected.contactName || '-'}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Phone:</strong> {selected.phone}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Status:</strong> {selected.status}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Last activity:</strong> {timeAgo(selected.lastMessageAt)}</div>
          <div style={{ fontSize: 13, marginBottom: 8 }}><strong>Tags:</strong> {(selected.tags || []).join(', ') || '-'}</div>
        </div>
      )}

      {showTest && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: 8, padding: 24, width: '90%', maxWidth: 460 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>🧪 Simulate Inbound Message</h3>
              <button onClick={() => setShowTest(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <p style={{ fontSize: 12, color: '#666' }}>Simulate a customer WhatsApp message. Auto-reply rules will fire if they match.</p>
            <input placeholder="Phone (+91...)" value={testForm.phone} onChange={e => setTestForm({ ...testForm, phone: e.target.value })} style={input} />
            <input placeholder="Name (optional)" value={testForm.name} onChange={e => setTestForm({ ...testForm, name: e.target.value })} style={input} />
            <textarea placeholder="Message text" value={testForm.text} onChange={e => setTestForm({ ...testForm, text: e.target.value })} style={{ ...input, minHeight: 80 }} />
            <button onClick={simulateInbound} style={btnPrimary}>Send Simulated Message</button>
          </div>
        </div>
      )}
    </div>
  );
}

const input = { padding: 8, border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, marginBottom: 8, width: '100%', boxSizing: 'border-box' };
const btnPrimary = { padding: '8px 16px', background: '#25D366', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 };
const miniBtn = { padding: '4px 10px', fontSize: 12, background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer' };
